<template>
	<div>
		<div class="cs-head">
			<div>
				<h1 class="cs-title">New quote</h1>
				<p class="cs-sub">Type the municipality or the account. The price list for its size, what it already has and what it pays come with it. Three steps: who, build, send.</p>
			</div>
			<div class="row"><pe-stepper :steps="['Who', 'Build', 'Send']" :current="1" /><router-link class="pill" to="/pricing/mine"><i class="fa-regular fa-list"></i> My quotes</router-link></div>
		</div>

		<div class="search big"><i class="fa-regular fa-magnifying-glass"></i><input ref="q" type="text" v-model="q" placeholder="Municipality or account, in any brand" @input="debounced" /></div>

		<div v-if="q.trim().length >= 2" class="tbl-scroll">
			<table class="tbl simple nochk">
				<tbody v-if="!searching">
					<tr v-for="r in results" :key="r.kind + r.municipality_id + '-' + r.client_id" class="rowable" @click="start(r)">
						<td class="sticky-l2"><div class="acct"><span class="avatar" :class="r.kind === 'account' ? 'tw' : ''">{{ initials(r.name) }}</span><span><span class="acct-name">{{ r.name }}<template v-if="r.state">, {{ r.state }}</template></span><span class="sub">{{ [kindOf(r), r.context || 'Prospect'].filter(Boolean).join(' · ') }}</span></span></div></td>
						<td class="muted num">{{ r.population ? 'pop ' + Number(r.population).toLocaleString() : '—' }}</td>
						<td><span v-for="p in r.products" :key="p" class="bchip" style="margin-right:4px"><span class="bdot" :class="chipClass(p)"></span>{{ p }}</span><span v-if="!r.products.length" class="dash">—</span></td>
						<td class="end"><button :class="r.kind === 'account' ? 'btn-primary' : 'pill'" :disabled="starting" @click.stop="start(r)">Quote</button></td>
					</tr>
					<tr v-if="!results.length" class="empty"><td colspan="4"><i class="fa-regular fa-folder-open"></i><div>Nothing matches. A town, village or city: add the municipality below, the population comes from the census. A county, a district, an association or a business: add it as an other organization.</div></td></tr>
				</tbody>
				<tbody v-else><tr v-for="n in 4" :key="n" class="skeleton"><td colspan="4"><span class="sk"></span></td></tr></tbody>
			</table>
		</div>

		<div class="qblock" style="margin-top:14px">
			<div class="qhead"><h5>Not in the list? <span class="sub">A municipality gets its population from the census. Anything else, a county, a district, an association, a business, is carried on the quote with the size you give it; nothing is added to the municipalities list.</span></h5>
				<span class="row"><button class="pill sm" :class="{ on: addOpen === 'municipality' }" @click="addOpen = addOpen === 'municipality' ? null : 'municipality'"><i class="fa-solid fa-plus"></i> Add a municipality</button><button class="pill sm" :class="{ on: addOpen === 'other' }" @click="addOpen = addOpen === 'other' ? null : 'other'"><i class="fa-solid fa-plus"></i> Other organization</button></span></div>
			<div v-if="addOpen === 'municipality'" class="row2" style="margin-top:12px;align-items:flex-end">
				<div class="ff"><label>Name</label><input v-model="add.name" placeholder="Village of Example" /></div>
				<div class="ff" style="max-width:120px"><label>State</label><input v-model="add.state" maxlength="2" placeholder="WI" /></div>
				<div class="ff"><label>Website (optional)</label><input v-model="add.website" placeholder="example.gov" /></div>
				<div class="ff" style="flex:0"><button class="btn-primary" :disabled="adding || !add.name || add.state.length !== 2" @click="addMunicipality">{{ adding ? 'Looking up…' : 'Add and quote' }}</button></div>
			</div>
			<div v-if="addOpen === 'other'" class="row2" style="margin-top:12px;align-items:flex-end;flex-wrap:wrap">
				<div class="ff"><label>Name</label><input v-model="other.name" placeholder="Dane County" /></div>
				<div class="ff" style="max-width:120px"><label>State</label><input v-model="other.state" maxlength="2" placeholder="WI" /></div>
				<div class="ff" style="max-width:200px"><label>What it is</label><select v-model="other.type"><option v-for="k in otherKinds" :key="k.key" :value="k.key">{{ k.label }}</option></select></div>
				<div class="ff" style="max-width:170px"><label>People served (optional)</label><input v-model="other.population" type="number" min="1" placeholder="Population or members" /></div>
				<div class="ff"><label>Where the number comes from (optional)</label><input v-model="other.note" placeholder="Census 2024, or 320 member households" /></div>
				<div class="ff" style="flex:0"><button class="btn-primary" :disabled="starting || !other.name || other.state.length !== 2 || !other.type" @click="addOther">Quote</button></div>
			</div>
			<p v-if="addOpen === 'other'" class="muted" style="font-size:12.5px;margin:8px 0 0">The price list follows the size you enter. Without a size the quote goes to finance, who set it.</p>
		</div>

		<h6 class="bd-grp" style="margin-top:22px">My recent quotes</h6>
		<div class="tbl-scroll" style="margin-top:6px">
			<table class="tbl nochk">
				<thead><tr><th class="sticky-l2">Account</th><th class="r">Price</th><th>Status</th><th>What to do</th><th>Updated</th><th class="end"></th></tr></thead>
				<tbody>
					<tr v-for="r in recent" :key="r.id" class="rowable" @click="open(r)">
						<td class="sticky-l2"><div class="acct"><span class="avatar">{{ initials(r.account) }}</span><span><span class="acct-name">{{ r.account }}<template v-if="r.state_abb">, {{ r.state_abb }}</template></span><span class="sub">{{ [r.prospect_type ? entityLabel(r.prospect_type) : '', r.context].filter(Boolean).join(' · ') }}</span></span></div></td>
						<td class="r num">{{ money(r.annual_net_cents) }} a year<template v-if="r.one_time_net_cents"> + {{ money(r.one_time_net_cents) }} once</template></td>
						<td><pe-status :tone="tone(r.next_step.status)" :label="r.next_step.status" /></td>
						<td class="wrap muted">{{ r.next_step.action }}</td>
						<td class="faint">{{ fmtDate(r.updated_at) }}</td>
						<td class="end"></td>
					</tr>
					<tr v-if="!recent.length && !loadingRecent" class="empty"><td colspan="6"><i class="fa-regular fa-folder-open"></i><div>No quotes yet. Find an account above and press Quote.</div></td></tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'
import { PeStepper, PeStatus } from '../../components/pe'
import { money, fmtDate, initials, errorText } from './pe-util'
import { ENTITY_KINDS, entityKind, entityLabel, entityLabelFor } from '../../utils/entity'

export default {
	name: 'PricingWho',
	components: { PeStepper, PeStatus },
	data() {
		return {
			q: '', results: [], searching: false, timer: null, starting: false, recent: [], loadingRecent: false,
			addOpen: null, add: { name: '', state: '', website: '' }, adding: false,
			other: { name: '', state: '', type: 'county', population: '', note: '' },
			// everything that is not a municipality, in the Accounts (Beta) vocabulary
			otherKinds: ENTITY_KINDS.filter(k => k.key !== 'municipality'),
		}
	},
	created() {
		// started from a client, account or municipality page: ?client=ID or ?municipality=ID goes straight to Build
		const qs = this.$route.query
		if (qs.client || qs.municipality) {
			this.start({ kind: qs.client ? 'account' : 'municipality', client_id: qs.client ? Number(qs.client) : null, municipality_id: qs.municipality ? Number(qs.municipality) : null, name: null })
			return
		}
		this.loadingRecent = true
		restApi.get('/pricing/quotes/mine', { params: { perPage: 6 } }).then(({ data }) => { this.recent = data.data }).catch(() => {}).finally(() => { this.loadingRecent = false })
	},
	mounted() { this.$nextTick(() => this.$refs.q && this.$refs.q.focus()) },
	methods: {
		money, fmtDate, initials, entityLabel,
		// the kind of an account when it is not a plain municipality: "County", "District", "Nonprofit & Org"
		kindOf(r) { const k = entityKind(r.entity); return k && k !== 'municipality' ? entityLabelFor(r.entity) : '' },
		debounced() { clearTimeout(this.timer); this.timer = setTimeout(this.search, 300) },
		search() {
			const q = this.q.trim()
			if (q.length < 2) { this.results = []; return }
			this.searching = true
			restApi.get('/pricing/search', { params: { q } }).then(({ data }) => { this.results = data }).catch(() => { this.results = [] }).finally(() => { this.searching = false })
		},
		chipClass(p) { return { Website: 'tw', HeyGov: 'hg', ClerkMinutes: 'cm', Vanteve: 'vt' }[p] || 'hg' },
		tone(status) { return { Draft: 'grey', 'Waiting for finance': 'amber', 'Ready to send': 'blue', Sent: 'green', Won: 'green', Lost: 'red', Expired: 'red', Closed: 'grey' }[status] || 'grey' },
		start(r) {
			this.starting = true
			restApi.post('/pricing/quotes', { client_id: r.client_id, municipality_id: r.municipality_id, prospect_name: r.kind === 'municipality' ? r.name : null })
				.then(({ data }) => this.$router.push('/pricing/quotes/' + data.id + '/build'))
				.catch(err => this.toast(errorText(err, 'Could not start the quote'))).finally(() => { this.starting = false })
		},
		addMunicipality() {
			this.adding = true
			restApi.post('/pricing/municipalities', { name: this.add.name, state: this.add.state.toUpperCase(), website: this.add.website || null })
				.then(({ data }) => this.start({ kind: 'municipality', client_id: null, municipality_id: data.municipality_id, name: data.name }))
				.catch(err => this.toast(errorText(err, 'Could not add the municipality'))).finally(() => { this.adding = false })
		},
		addOther() {
			this.starting = true
			const pop = parseInt(this.other.population, 10)
			const notes = [entityLabel(this.other.type).toLowerCase(), pop > 0 ? 'people served: ' + pop.toLocaleString() + (this.other.note ? ' (' + this.other.note + ')' : '') : (this.other.note || '')].filter(Boolean).join(' · ')
			restApi.post('/pricing/quotes', { prospect_name: this.other.name.trim(), prospect_state: this.other.state.toUpperCase(), prospect_type: this.other.type, population: pop > 0 ? pop : null, notes: notes || null })
				.then(({ data }) => this.$router.push('/pricing/quotes/' + data.id + '/build'))
				.catch(err => this.toast(errorText(err, 'Could not start the quote'))).finally(() => { this.starting = false })
		},
		open(r) { this.$router.push('/pricing/quotes/' + r.id + (['draft', 'waiting_inputs'].includes(r.state) ? '/build' : '/send')) },
		toast(title, icon = 'error') { this.$root.$emit('pe-notice', { tone: icon === 'success' ? 'ok' : (icon === 'info' ? 'info' : 'warn'), text: title }) },
	},
}
</script>
